import { Avatar, Description, Dropdown, Label, Separator } from "@heroui/react";
import { Link } from "react-router";
import logo from "../assets/brand/loghi-politor-excellera.svg";
import { Icon } from "./Icon";
import { versionLabel } from "../config/site";
import type { User } from "../data/mock";

export type MainSection = "news" | "ricerca" | "alert";

const NAV: { id: MainSection; label: string }[] = [
  { id: "news", label: "News" },
  { id: "ricerca", label: "Ricerca" },
  { id: "alert", label: "Gestione alert" },
];

type AppHeaderProps = {
  user: User;
  /** The section the page belongs to; null for a page of none (the guide). */
  section: MainSection | null;
  onSectionChange: (section: MainSection) => void;
  /** «Guida a Politor» in the user menu: opens the guide (/guida). */
  onGuide: () => void;
  /** Absent in dev mode (a fixed test user): the menu has no «Esci». */
  onLogout?: () => void;
  /** The build config.js names (a commit, or dev): at the bottom of the menu, with the package's version. */
  build: string;
};

export function AppHeader({ user, section, onSectionChange, onGuide, onLogout, build }: AppHeaderProps) {
  return (
    <header className="bg-brand">
      <div className="relative mx-auto flex h-20 max-w-[1480px] items-center justify-between px-4">
        <div className="flex items-center gap-4">
          {/* The logo is the way home: «Ultime news» without filters, from any page */}
          <Link to="/news" className="block rounded-sm focus-visible:outline-2 focus-visible:outline-white">
            <img src={logo} alt="Politor AI | Excellera" width={266} height={43} className="block" />
          </Link>
          {/* While PolitorAI is in beta (see config/site.ts): the logo's yellow, the brand's blue */}
          <span className="rounded-full bg-brand-yellow px-2.5 py-0.5 text-xs leading-5 font-semibold text-brand">
            Versione Beta
          </span>
        </div>

        <nav aria-label="Navigazione principale" className="absolute left-1/2 flex -translate-x-1/2 items-center gap-[50px]">
          {NAV.map((item) => {
            const active = item.id === section;
            return (
              <button
                key={item.id}
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => onSectionChange(item.id)}
                className={`cursor-pointer rounded-sm px-2 py-1 text-base leading-7 text-white transition-colors hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-white ${
                  active ? "bg-accent-hover font-medium" : "font-normal"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

          <Dropdown>
          <Dropdown.Trigger aria-label="Menu utente" className="cursor-pointer rounded-full">
            <Avatar className="size-10">
              <Avatar.Fallback className="bg-default text-xs font-medium text-accent-soft-foreground">
                {user.initials}
              </Avatar.Fallback>
            </Avatar>
          </Dropdown.Trigger>
          {/* 240px, wider up to 352px for a long address, which then wraps */}
          <Dropdown.Popover placement="bottom end" className="min-w-60 max-w-[22rem] rounded-xl">
            <Dropdown.Menu
              aria-label="Menu utente"
              onAction={(key) => {
                if (key === "logout") onLogout?.();
                else if (key === "guide") onGuide();
              }}
            >
              <Dropdown.Item id="profile" textValue={user.name} className="rounded-[20px] px-3 py-1.5">
                <div className="flex min-w-0 flex-col">
                  <Label className="text-sm leading-6 font-medium text-accent">{user.name}</Label>
                  <Description className="text-xs leading-5 text-muted [overflow-wrap:anywhere]">{user.email}</Description>
                </div>
              </Dropdown.Item>
              <Dropdown.Item id="guide" textValue="Guida a Politor" className="gap-3 rounded-[20px] px-3 py-1.5">
                <Label className="flex-1 text-sm leading-6 font-medium text-accent">Guida a Politor</Label>
                <Icon name="arrowRight" />
              </Dropdown.Item>
              {onLogout && <Separator className="mx-3 my-0.5 w-auto" />}
              {onLogout && (
                <Dropdown.Item id="logout" textValue="Esci" className="rounded-[20px] px-3 py-1.5">
                  <Label className="text-sm leading-6 font-medium text-accent">Esci</Label>
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
            {/* The build, under the items: text to read, nothing to choose */}
            <Separator className="mx-3 my-0.5 w-auto" />
            <p className="px-3 pt-1 pb-1.5 text-xs leading-5 text-muted">{versionLabel(build)}</p>
          </Dropdown.Popover>
        </Dropdown>
      </div>
    </header>
  );
}
